import { FollowUpCategoryTypes, FollowUpStatusTypes, FollowUpTaskTypes, FollowUpTypes } from '@/shared/api/graphql/account';
import type { FollowUpRemindersTypes } from '@/shared/lib/types/accountProfile';
import { MedicalServices } from '@/shared/lib/types/admin';
import type { TruentityDateInput } from '@/shared/lib/types/date';
import type { ReminderConfigurationsType, ReminderMessageTemplateOption, TemplateVariable } from '@/shared/lib/types/followup';
import { VisitMode } from '@/shared/lib/types/followup';
import { formatDate, formatTime } from '@/shared/lib/util/format';
import { color } from '@/shared/styles/assets/colors';
import type { Order } from '@/shared/ui/components/EnhancedTableHead';
import type { TextSelectOption } from '@/shared/ui/SelectList';
import type { EventSourceInput } from '@fullcalendar/core';
import moment from 'moment';
import { getAccountUserFullName } from './account';

export enum FollowupFormModeTypes {
  UPDATE = 'UPDATE',
  ADD = 'ADD'
}

// Display names for follow-up types whose stored value is an older label.
const followUpTypeLabels: Partial<Record<string, string>> = {
  [FollowUpTypes.RPM_INITIAL_VISIT]: 'New Patient Visit'
};

export const followUpTypeLabel = (type: string | null | undefined): string => (type ? followUpTypeLabels[type] ?? type : '');

export function followUpTypes(
  doNotCall?: boolean,
  isEnrolled?: boolean,
  isUnenrolled?: boolean,
  currentMedicalServices?: string[],
  /** CCM-only enrollment on at a CCM pharmacy: offer the CCM visit even without RPM (D6). */
  canEnrollCcmOnly = false
): TextSelectOption[] {
  const options: TextSelectOption[] = [
    { label: FollowUpTypes.CALL_PATIENT, value: FollowUpTypes.CALL_PATIENT, disabled: doNotCall ?? false },
    { label: FollowUpTypes.CALL_PROVIDER, value: FollowUpTypes.CALL_PROVIDER },
    { label: FollowUpTypes.TEXT_PATIENT, value: FollowUpTypes.TEXT_PATIENT, disabled: doNotCall ?? false },
    { label: FollowUpTypes.REVIEW_CHART, value: FollowUpTypes.REVIEW_CHART },
    { label: FollowUpTypes.REVIEW, value: FollowUpTypes.REVIEW }
  ];

  if (currentMedicalServices?.includes(MedicalServices.RPM)) {
    options.push(
      {
        label: followUpTypeLabel(FollowUpTypes.RPM_INITIAL_VISIT),
        value: FollowUpTypes.RPM_INITIAL_VISIT,
        disabled: isUnenrolled
      },
      {
        label: FollowUpTypes.PHARMACY_VISIT,
        value: FollowUpTypes.PHARMACY_VISIT
      },
      {
        label: FollowUpTypes.CCM_INITIAL_VISIT,
        value: FollowUpTypes.CCM_INITIAL_VISIT
      },
      {
        label: FollowUpTypes.FOLLOW_UP_VISIT,
        value: FollowUpTypes.FOLLOW_UP_VISIT
      }
    );
  } else if (canEnrollCcmOnly) {
    options.push({ label: FollowUpTypes.CCM_INITIAL_VISIT, value: FollowUpTypes.CCM_INITIAL_VISIT });
  }

  return options;
}

export function followUpTaskTypes(
  category: FollowUpCategoryTypes | 'ALL',
  formMode: FollowupFormModeTypes = FollowupFormModeTypes.ADD
): TextSelectOption[] {
  const baseTasks: TextSelectOption[] = [{ label: FollowUpTaskTypes.CONTACT_PATIENT, value: FollowUpTypes.CALL_PATIENT, disabled: false }];

  const categoryTasks: Record<FollowUpCategoryTypes | 'ALL', TextSelectOption[]> = {
    [FollowUpCategoryTypes.GENERAL]: [],
    // Empty on purpose. The return below prepends baseTasks for every category except GENERAL, so
    // an Obtain Consent task offers exactly one Task Type: "Contact Patient", which stores as
    // FollowUpTypes.CALL_PATIENT.
    [FollowUpCategoryTypes.OBTAIN_CONSENT]: [],
    [FollowUpCategoryTypes.ALERT_REVIEW]: [
      {
        label: FollowUpTaskTypes.CONTACT_PRIMARY_CARE_PROVIDER,
        value: FollowUpTaskTypes.CONTACT_PRIMARY_CARE_PROVIDER,
        disabled: false
      }
    ],

    [FollowUpCategoryTypes.CHART_REVIEW]: [
      {
        label: FollowUpTaskTypes.CONTINUE_CURRENT_MONITORING_AND_CATE_PLAN,
        value: FollowUpTaskTypes.CONTINUE_CURRENT_MONITORING_AND_CATE_PLAN,
        disabled: false
      },
      {
        label: FollowUpTaskTypes.CONTACT_CARE_TEAM_MEMBER,
        value: FollowUpTaskTypes.CONTACT_CARE_TEAM_MEMBER,
        disabled: false
      },
      {
        label: FollowUpTaskTypes.NO_INTERVENTION_REQUIRED,
        value: FollowUpTaskTypes.NO_INTERVENTION_REQUIRED,
        disabled: formMode === FollowupFormModeTypes.UPDATE
      }
    ],

    ALL: [
      {
        label: FollowUpTaskTypes.CONTACT_PRIMARY_CARE_PROVIDER,
        value: FollowUpTaskTypes.CONTACT_PRIMARY_CARE_PROVIDER,
        disabled: false
      },
      {
        label: FollowUpTaskTypes.CONTINUE_CURRENT_MONITORING_AND_CATE_PLAN,
        value: FollowUpTaskTypes.CONTINUE_CURRENT_MONITORING_AND_CATE_PLAN,
        disabled: false
      },
      {
        label: FollowUpTaskTypes.CONTACT_CARE_TEAM_MEMBER,
        value: FollowUpTaskTypes.CONTACT_CARE_TEAM_MEMBER,
        disabled: false
      },
      {
        label: FollowUpTaskTypes.NO_INTERVENTION_REQUIRED,
        value: FollowUpTaskTypes.NO_INTERVENTION_REQUIRED,
        disabled: formMode === FollowupFormModeTypes.UPDATE
      }
    ]
  };

  return category === FollowUpCategoryTypes.GENERAL ? categoryTasks[category] : [...baseTasks, ...categoryTasks[category]];
}

export function followUpSubTypes(): TextSelectOption[] {
  return [
    { label: 'None', value: 'None' },
    { label: 'RPM', value: 'RPM' },
    { label: 'CCM', value: 'CCM' },
    { label: 'CMR', value: 'CMR' },
    { label: 'TIP', value: 'TIP' },
    { label: 'Adherence', value: 'Adherence' }
  ];
}

export function followUpStatuses(): TextSelectOption[] {
  return [
    { label: 'Pending', value: FollowUpStatusTypes.PENDING },
    { label: 'Completed', value: FollowUpStatusTypes.COMPLETED }
  ];
}

export function clusterFollowupsByDate(followups: FollowUpRemindersTypes[], sortOrder: Order = 'asc') {
  const groupedByDate = followups.reduce((acc, followup) => {
    const dateKey = moment(followup.followUpOn).format('YYYY-MM-DD');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(followup);
    return acc;
  }, {} as { [date: string]: FollowUpRemindersTypes[] });

  const dates = Object.keys(groupedByDate).sort((a, b) => {
    return sortOrder === 'asc' ? moment(a).diff(moment(b)) : moment(b).diff(moment(a));
  });
  return groupedByDate[dates[0]] || [];
}

export function followupTypeHandler(followup: FollowUpRemindersTypes) {
  const { type, category } = followup;
  if (type !== FollowUpTypes.CALL_PATIENT) return followUpTypeLabel(type);

  if (category === FollowUpCategoryTypes.GENERAL) return type;

  return FollowUpTaskTypes.CONTACT_PATIENT;
}

export const getEffectiveReminderConfigurations = (
  configurations: ReminderConfigurationsType[] | undefined,
  visitMode: VisitMode
): ReminderConfigurationsType[] => {
  if (!configurations || configurations.length === 0) {
    return [];
  }

  const isCommon = (config: ReminderConfigurationsType) => config.reminderMode === null || config.reminderMode === undefined;

  const byDaysBefore = new Map<number, ReminderConfigurationsType>();
  configurations.forEach(config => {
    const matchesMode = config.reminderMode === visitMode;
    if (!isCommon(config) && !matchesMode) {
      return;
    }

    const existing = byDaysBefore.get(config.daysBefore);
    if (!existing || (isCommon(existing) && matchesMode)) {
      byDaysBefore.set(config.daysBefore, config);
    }
  });

  return Array.from(byDaysBefore.values());
};

export type GetReminderMessagePreviewParams = {
  message: string;
  templateVariables: TemplateVariable[];
};

export const getReminderMessagePreview = ({ message, templateVariables }: GetReminderMessagePreviewParams): string => {
  if (!message || !templateVariables?.length) {
    return message;
  }

  let preview = message;

  templateVariables.forEach(variable => {
    const regex = new RegExp(variable.key, 'g');
    preview = preview.replace(regex, variable.value);
  });

  return preview;
};

/**
 * The templates offered for a visit mode, in the Backend's order. When the Backend sends no options
 * (an older API), the single legacy template for that mode is offered instead.
 */
export const getTemplateOptionsForMode = (
  options: ReminderMessageTemplateOption[] | undefined,
  mode: VisitMode,
  fallbackTemplate?: string
): ReminderMessageTemplateOption[] => {
  const optionsForMode = (options ?? []).filter(option => option.mode === mode);
  if (optionsForMode.length > 0) {
    return optionsForMode;
  }

  return fallbackTemplate ? [{ mode, label: '', template: fallbackTemplate, isAvailable: true, unavailableReason: null }] : [];
};

/** Index of the first option that can be used, or -1 when none can. */
export const getDefaultTemplateOptionIndex = (options: ReminderMessageTemplateOption[]): number =>
  options.findIndex(option => option.isAvailable);

export const visitModeToDisplayText = (mode?: VisitMode): string => {
  switch (mode) {
    case VisitMode.IN_PERSON:
      return 'in-person';
    case VisitMode.VIRTUAL:
      return 'virtual';
    default:
      return '';
  }
};

export type GetTemplateVariablesParams = {
  templateVariables: TemplateVariable[];
  appointmentDate?: TruentityDateInput;
  appointmentTime?: TruentityDateInput;
  appointmentType?: string;
  visitMode?: VisitMode;
};

export const getUpdatedTemplateVariables = ({
  templateVariables,
  appointmentDate,
  appointmentTime,
  appointmentType,
  visitMode
}: GetTemplateVariablesParams): TemplateVariable[] => {
  const updatedVariables = [...templateVariables];
  const visitTypeIndex = updatedVariables.findIndex(v => v.key === '#{VISIT_TYPE}');
  if (visitTypeIndex >= 0) {
    updatedVariables[visitTypeIndex] = {
      ...updatedVariables[visitTypeIndex],
      value: followUpTypeLabel(appointmentType)
    };
  }

  const visitModeIndex = updatedVariables.findIndex(v => v.key === '#{VISIT_MODE}');
  if (visitModeIndex >= 0 && visitMode) {
    updatedVariables[visitModeIndex] = {
      ...updatedVariables[visitModeIndex],
      value: visitModeToDisplayText(visitMode)
    };
  }

  const dateIndex = updatedVariables.findIndex(v => v.key === '#{APPOINTMENT_DATE}');
  if (dateIndex >= 0) {
    updatedVariables[dateIndex] = {
      ...updatedVariables[dateIndex],
      value: appointmentDate ? formatDate(appointmentDate, 'MMM DD, YYYY') : ''
    };
  }

  const timeIndex = updatedVariables.findIndex(v => v.key === '#{APPOINTMENT_TIME}');
  if (timeIndex >= 0) {
    updatedVariables[timeIndex] = {
      ...updatedVariables[timeIndex],
      value: appointmentTime ? formatDate(appointmentTime, 'hh:mm A') : ''
    };
  }

  return updatedVariables;
};

export const followupEventColorMapping: Record<string, Record<string, string>> = {
  [FollowUpCategoryTypes.ALERT_REVIEW]: {
    [FollowUpStatusTypes.PENDING]: color.purple500,
    [FollowUpStatusTypes.COMPLETED]: color.purple700
  },
  [FollowUpCategoryTypes.CHART_REVIEW]: {
    [FollowUpStatusTypes.PENDING]: color.indigo400,
    [FollowUpStatusTypes.COMPLETED]: color.indigo800
  },
  [FollowUpTypes.RPM_INITIAL_VISIT]: {
    [FollowUpStatusTypes.PENDING]: color.truentityBlue.A200,
    [FollowUpStatusTypes.COMPLETED]: color.truentityCyan[300]
  },
  default: {
    [FollowUpStatusTypes.PENDING]: color.blue500,
    [FollowUpStatusTypes.COMPLETED]: color.green500
  }
};

export const getFollowupEventColor = (followup: FollowUpRemindersTypes) => {
  const { status, type, category } = followup;
  const categoryColors = followupEventColorMapping[category] || followupEventColorMapping[type] || followupEventColorMapping['default'];
  return categoryColors[status] || color.blue500;
};

export const transformAccountsFollowupToCalendarEvents = (
  followupReminders: FollowUpRemindersTypes[],
  timezoneText: string
): EventSourceInput => {
  const events = followupReminders.map(followupReminder => {
    return {
      uuid: followupReminder.id,
      status: followupReminder.status,
      title: getAccountUserFullName(followupReminder.account.user).toUpperCase(),
      time: formatTime(followupReminder.followUpOn, 'hh:mm A', timezoneText),
      eventTime: formatTime(followupReminder.followUpOn, 'HH:mm'),
      start: followupReminder.followUpOn,
      type: followupReminder.type,
      subType: followupReminder.subType,
      category: followupReminder.category,
      notes: followupReminder.notes,
      allDay: true,
      id: followupReminder.id,
      relyingPartyAdmin: followupReminder.relyingPartyAdmin,
      extendedProps: {
        patientData: followupReminder.account
      },
      color: getFollowupEventColor(followupReminder)
    };
  });

  return events;
};
